import { useMutation, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import {
  deleteNotificationAll,
  markNotificationAsRead,
  updateIotDeviceNotificationSetting,
  updateNotificationSettingProfile,
  updateNotificationSettingThreshold,
} from '.'
import {
  useGetNotifications,
  useGetNotificationsSettingsByModule,
  useGetNotificationUnreadCount,
} from './queries'
import {
  getIotDeviceNotificationSettingKey,
  getNotificationUnreadCountKey,
} from 'common/constants/queryKeys'

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient()
  const { refetch: refetchUnreadCount } = useGetNotificationUnreadCount()
  const { refetch: refetchNotifications } = useGetNotifications()

  return useMutation(markNotificationAsRead, {
    onSuccess: (res) => {
      const status = (res as any)?.status
      if (status !== 200 && status !== 204) return

      try {
        queryClient.invalidateQueries(['notifications'])
        queryClient.invalidateQueries(getNotificationUnreadCountKey())
      } catch (e) {
        refetchUnreadCount()
        refetchNotifications()
      }
    },
  })
}

export const useDeleteNotificationAll = () => {
  const queryClient = useQueryClient()
  const { refetch: refetchUnreadCount } = useGetNotificationUnreadCount()
  const { refetch: refetchNotifications } = useGetNotifications()

  return useMutation(deleteNotificationAll, {
    onSuccess: (res) => {
      const status = (res as any)?.status
      if (status !== 200 && status !== 204) return

      try {
        queryClient.invalidateQueries(['notifications'])
        queryClient.invalidateQueries(getNotificationUnreadCountKey())
      } catch (e) {
        refetchUnreadCount()
        refetchNotifications()
      }
    },
  })
}


export const useUpdateNotificationSettingThreshold = (moduleCode?: string) => {
  const { refetch } = useGetNotificationsSettingsByModule(moduleCode)
  const { t } = useTranslation(['common'])

  return useMutation(
    ({ id, payload }: { id: string; payload?: Record<string, any> }) =>
      updateNotificationSettingThreshold(id, payload),

    {
      onSuccess: (res) => {
        if (res.status === 200) {
          enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
          // refetch()
        }
      },
      onError: (err) => {
        console.error('Failed to update notification setting:', err)
      },
    }
  )
}

// DOF-1309: บันทึกสวิตช์แจ้งเตือนของอุปกรณ์ IoT — ไม่ยิง snackbar เองที่นี่
// เพราะหน้า threshold ต้องรวมข้อความกับผลของ mutation อีกตัว (threshold เอง)
// เข้าด้วยกัน เนื่องจากปุ่ม "ยืนยัน" เดียวคุมสอง endpoint ที่ failure mode
// ไม่เหมือนกัน (ดู DOF-1309)
export const useUpdateIotDeviceNotificationSetting = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ deviceId, isEnabled }: { deviceId: string; isEnabled: boolean }) =>
      updateIotDeviceNotificationSetting(deviceId, isEnabled),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: getIotDeviceNotificationSettingKey(variables.deviceId),
      })
    },
  })
}

export const useUpdateNotificationSettingProfile = (moduleCode?: string) => {
  const { refetch } = useGetNotificationsSettingsByModule(moduleCode)
  const { t } = useTranslation(['common'])

  return useMutation(
    ({ id, payload }: { id: string; payload?: Record<string, any> }) =>
      updateNotificationSettingProfile(id, payload),

    {
      onSuccess: (res) => {
        if (res.status === 200) {
          enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
          refetch()
        }
      },
      onError: (err) => {
        console.error('Failed to update notification setting:', err)
      },
    }
  )
}
